import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { layerWindowToVideoSegments } from '@/lib/layers/layerWindowToVideoSegments';
import { resolveLayerSeekTarget } from '@/lib/layers/resolveLayerSeekTarget';
import { EFFECT_LIBRARY } from '@/lib/video-effects/library/registry';
import { EFFECT_LANE_HEIGHT, moveSectionWindow, packEffectRows, trimSectionWindow } from '@/lib/video-effects/sectionTimeline';
import type { EffectSection } from '@/lib/video-effects/sections';

interface Props {
  sections: EffectSection[];
  activeSectionId: string | null;
  intervals: Array<{ startTime: number; endTime: number }>;
  pxPerSecond: number;
  sequenceMode?: boolean;
  /** Vertical px offset of the first effect row inside the timeline container. */
  laneTop: number;
  clipDuration: number;
  onSelect: (id: string | null) => void;
  /** Fired ONCE per drag, on pointerup — one undo entry and one save per gesture. */
  onCommit: (id: string, window: { start: number; end: number }) => void;
  /** Playhead in source-video time. */
  currentTime?: number;
  onSeek?: (videoTime: number) => void;
}

/** Below this much pointer travel a press is a click, not a drag. */
const DRAG_THRESHOLD_PX = 4;
type Mode = 'move' | 'trim-start' | 'trim-end';
type Draft = { id: string; start: number; end: number };

/** Effect section bars on the ruler's source-video axis (like LayerLanes).
 *  Drags keep a local draft and commit on pointerup only. */
export function EffectLanes({
  sections, activeSectionId, intervals, pxPerSecond, sequenceMode = false, laneTop, clipDuration,
  onSelect, onCommit, currentTime = 0, onSeek,
}: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const suppressClickRef = useRef(false);
  const suppressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ pxPerSecond, clipDuration, onCommit });
  latest.current = { pxPerSecond, clipDuration, onCommit };

  // An unmount mid-drag must not leak window listeners.
  useEffect(() => () => {
    cleanupRef.current?.();
    if (suppressTimerRef.current != null) clearTimeout(suppressTimerRef.current);
  }, []);

  const beginDrag = useCallback((e: React.PointerEvent, section: EffectSection, mode: Mode) => {
    if (e.button !== 0) return; // right-click belongs to the context menu
    e.preventDefault(); // no text selection / native drag while dragging a bar
    e.stopPropagation();
    suppressClickRef.current = false; // a stale flag must never swallow a new gesture
    if (suppressTimerRef.current != null) clearTimeout(suppressTimerRef.current);
    const startX = e.clientX;
    let moved = false;
    let pending: Draft | null = null;
    let raf: number | null = null;
    const compute = (clientX: number): Draft => {
      const deltaSec = (clientX - startX) / latest.current.pxPerSecond;
      const w = mode === 'move'
        ? moveSectionWindow(section, deltaSec, latest.current.clipDuration)
        : trimSectionWindow(section, mode === 'trim-start' ? 'start' : 'end', deltaSec, latest.current.clipDuration);
      return { id: section.id, ...w };
    };
    const onMove = (ev: PointerEvent) => {
      if (!moved && Math.abs(ev.clientX - startX) < DRAG_THRESHOLD_PX) return;
      moved = true;
      pending = compute(ev.clientX);
      if (raf != null) return;
      raf = requestAnimationFrame(() => { raf = null; if (pending) setDraft(pending); });
    };
    const finish = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', finish);
      if (raf != null) { cancelAnimationFrame(raf); raf = null; }
      cleanupRef.current = null;
      setDraft(null);
    };
    const onUp = () => {
      const result = moved ? pending : null;
      finish();
      if (result) {
        suppressClickRef.current = true; // the click that follows a drag is not a selection
        // If the pointer ended off the bar no click fires; don't leave the flag armed.
        suppressTimerRef.current = setTimeout(() => { suppressClickRef.current = false; suppressTimerRef.current = null; }, 0);
        latest.current.onCommit(result.id, { start: result.start, end: result.end });
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', finish);
    cleanupRef.current = finish;
  }, []);

  const rows = packEffectRows(sections);
  return (
    <>
      {sections.map((section, idx) => {
        const live = draft?.id === section.id ? { ...section, start: draft.start, end: draft.end } : section;
        const segments = sequenceMode
          ? [{ videoStart: live.start, videoEnd: live.end }]
          : layerWindowToVideoSegments(live.start, live.end, intervals);
        const top = laneTop + rows[idx] * EFFECT_LANE_HEIGHT;
        const entry = EFFECT_LIBRARY[section.effect];
        const label = t(entry.labelKey);
        const color = entry.category === 'dynamic' ? 'bg-amber-500/40 border-amber-500' : 'bg-sky-500/40 border-sky-500';
        const active = section.id === activeSectionId;
        return segments.map((seg, si) => (
          <div
            key={`${section.id}-${si}`}
            data-testid="effect-lane-bar"
            data-layer-ui
            className={`absolute h-4 cursor-grab rounded border ${color} ${active ? 'ring-1 ring-white' : ''}`}
            style={{ top, left: seg.videoStart * pxPerSecond, width: Math.max(4, (seg.videoEnd - seg.videoStart) * pxPerSecond) }}
            title={label}
            onPointerDown={(e) => beginDrag(e, section, 'move')}
            onClick={(e) => {
              e.stopPropagation();
              if (suppressClickRef.current) { suppressClickRef.current = false; return; }
              onSelect(section.id);
              const target = resolveLayerSeekTarget(section, intervals, currentTime);
              if (target != null) onSeek?.(target);
            }}
          >
            {si === 0 && (
              <span className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 overflow-hidden whitespace-nowrap text-[10px] leading-none text-white">
                {label}
              </span>
            )}
            {si === 0 && (
              <div className="absolute left-0 top-0 h-full w-1.5 cursor-ew-resize bg-white/60"
                onPointerDown={(e) => beginDrag(e, section, 'trim-start')} />
            )}
            {si === segments.length - 1 && (
              <div className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize bg-white/60"
                onPointerDown={(e) => beginDrag(e, section, 'trim-end')} />
            )}
          </div>
        ));
      })}
    </>
  );
}
